import React, { useRef, useEffect, useState } from 'react';
import backgroundImage from './assets/background.png';

const FireExtinguisherCamera = ({ currentUser, userPoints, userRank, soundEnabled, onToggleSound, onNavigateLeaderboard, onExit, onCapture, onAdminAccess }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const dropdownRef = useRef(null);
    const fileInputRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [facingMode, setFacingMode] = useState('environment');
    const [stream, setStream] = useState(null);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const [isMounted, setIsMounted] = useState(true);

    const openCamera = async (facing = facingMode) => {
        console.log(`FireExtinguisherCamera: Attempting to open camera with facing mode: ${facing}...`);
        try {
            setIsLoading(true);
            setError(null);
            
            // Stop existing stream
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
            
            console.log("FireExtinguisherCamera: Requesting media devices at MAXIMUM resolution...");
            // 🔧 HIGH-RES FIX: Request maximum resolution for best OCR/AI accuracy
            // Modern phones support 4K (3840×2160) or higher - we want the best image for AI
            const newStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: facing,
                    aspectRatio: { ideal: 0.75 },         // 3:4 portrait (prevents fisheye on mobile)
                    width: { ideal: 1920 },               // Request 1080p (no min - flexible for web)
                    height: { ideal: 2560 }               // Portrait (no min - flexible for web)
                }
            });
            
            // Check if component is still mounted
            if (!isMounted) {
                newStream.getTracks().forEach(track => track.stop());
                return;
            }
            
            setStream(newStream);
                
            console.log("FireExtinguisherCamera: Camera stream obtained.", newStream);

            if (videoRef.current) {
                videoRef.current.srcObject = newStream;
                console.log("FireExtinguisherCamera: Video source object set.");

                // Add loaded metadata listener for more reliable playback
                videoRef.current.onloadedmetadata = () => {
                    if (!videoRef.current || !isMounted) return;
                    
                    // 📐 LOG ACTUAL STREAM RESOLUTION (for debugging)
                    const actualWidth = videoRef.current.videoWidth;
                    const actualHeight = videoRef.current.videoHeight;
                    console.log(`📹 FireExtinguisherCamera: Stream granted at ${actualWidth}×${actualHeight}`);
                    
                    const playPromise = videoRef.current.play();

                    if (playPromise !== undefined) {
                        playPromise.then(() => {
                            if (!isMounted) return;
                            console.log("FireExtinguisherCamera: Camera preview is playing.");
                            setIsLoading(false);
                        }).catch(error => {
                            if (!isMounted) return;
                            // Ignore AbortError - it's usually a race condition
                            if (error.name === 'AbortError') {
                                console.warn("FireExtinguisherCamera: Play aborted (likely due to quick navigation), retrying...");
                                setTimeout(() => {
                                    if (videoRef.current && isMounted) {
                                        videoRef.current.play().catch(() => {});
                                    }
                                }, 100);
                                setIsLoading(false);
                                return;
                            }
                            console.error("FireExtinguisherCamera: Error attempting to play video:", error);
                            setError("Camera error. Please try again.");
                            setIsLoading(false);
                        });
                    } else {
                        setIsLoading(false);
                    }
                };
            } else {
                console.error("FireExtinguisherCamera: videoRef is not available.");
                setError("Video element not available.");
                setIsLoading(false);
            }
        } catch (error) {
            if (!isMounted) return;
            
            console.error("FireExtinguisherCamera: Error accessing camera:", error);
            let errorMessage = 'Unable to access camera. ';
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                errorMessage += 'Please allow camera permission in your browser settings and try again.';
            } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                errorMessage += 'No camera found on this device.';
            } else if (error.name === 'NotReadableError') {
                errorMessage += 'Camera is already in use by another application.';
            } else if (error.name === 'OverconstrainedError') {
                errorMessage += 'Requested camera mode not available. Trying alternative...';
                const fallbackMode = facing === 'environment' ? 'user' : 'environment';
                console.log(`Trying fallback camera mode: ${fallbackMode}`);
                setTimeout(() => openCamera(fallbackMode), 1000);
                return;
            } else {
                errorMessage += 'An unexpected error occurred.';
            }
            setError(errorMessage);
            setIsLoading(false);
        }
    };

    const switchCamera = async () => {
        const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';
        setFacingMode(newFacingMode);
        await openCamera(newFacingMode);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setIsMounted(true);
        openCamera();

        return () => {
            setIsMounted(false);
            if (stream) {
                console.log("FireExtinguisherCamera: Closing camera stream.");
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowProfileDropdown(false);
            }
        };

        if (showProfileDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showProfileDropdown]);

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        console.log("FireExtinguisherCamera: File selected for upload");
        
        const reader = new FileReader();
        reader.onload = (e) => {
            const imageData = e.target.result;
            console.log("FireExtinguisherCamera: File converted to base64, triggering onCapture");
            
            if (onCapture) {
                onCapture(imageData);
            }
        };
        reader.readAsDataURL(file);
    };

    const capturePhoto = () => {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        
        if (!video || !canvas || !video.srcObject) {
            console.error("FireExtinguisherCamera: Cannot capture - video or canvas not available");
            return;
        }
        
        // 📐 LOG ACTUAL CAPTURE RESOLUTION (for debugging AI quality issues)
        const nativeWidth = video.videoWidth;
        const nativeHeight = video.videoHeight;
        console.log(`📷 FireExtinguisherCamera: Capturing photo at NATIVE resolution: ${nativeWidth}×${nativeHeight}`);
        
        const context = canvas.getContext('2d');
        
        canvas.width = nativeWidth;
        canvas.height = nativeHeight;
        
        context.drawImage(video, 0, 0, nativeWidth, nativeHeight);
        
        // 🔧 HIGH-RES PORTRAIT FIX: Maintain fire tag portrait aspect ratio (3:5) at higher resolution
        // Fire extinguisher tags are portrait orientation - we need portrait output regardless of camera orientation
        // Camera gives us high resolution source, we crop to portrait fire tag shape
        
        // Helper function to create cropped canvas at specific size
        const createCroppedImage = (width, height, quality) => {
            const croppedCanvas = document.createElement('canvas');
            const croppedContext = croppedCanvas.getContext('2d');
            
            croppedCanvas.width = width;
            croppedCanvas.height = height;
            
            const scaleX = width / nativeWidth;
            const scaleY = height / nativeHeight;
            const scale = Math.max(scaleX, scaleY);
            
            const scaledWidth = nativeWidth * scale;
            const scaledHeight = nativeHeight * scale;
            
            const offsetX = (width - scaledWidth) / 2;
            const offsetY = (height - scaledHeight) / 2;
            
            croppedContext.save();
            croppedContext.beginPath();
            
            // Clip path for fire tag shape (scaled to canvas size)
            const clipPath = [
                [0, height * 0.04],
                [width * 0.04, 0],
                [width * 0.96, 0],
                [width, height * 0.04],
                [width, height],
                [0, height]
            ];
            
            croppedContext.moveTo(clipPath[0][0], clipPath[0][1]);
            for (let i = 1; i < clipPath.length; i++) {
                croppedContext.lineTo(clipPath[i][0], clipPath[i][1]);
            }
            croppedContext.closePath();
            croppedContext.clip();
            
            croppedContext.drawImage(video, offsetX, offsetY, scaledWidth, scaledHeight);
            croppedContext.restore();
            
            return croppedCanvas.toDataURL('image/jpeg', quality);
        };
        
        // 📐 RESOLUTION STRATEGY:
        // - Fire tags are PORTRAIT orientation (3:5 aspect ratio)
        // - Full-res: 1200×2000 (higher than before for better AI OCR, but portrait)
        // - Thumbnail: 300×500 for list views (same portrait ratio)
        // - Camera request is high-res (4K) for sharpest source, then we crop to portrait
        
        // Create thumbnail (300×500) for lists and previews
        const thumbnailData = createCroppedImage(300, 500, 0.85);
        
        // Create full-resolution image for AI processing (1200×2000 portrait for fire tags)
        const fullResData = createCroppedImage(1200, 2000, 0.92);
        
        console.log(`📷 FireExtinguisherCamera: Captured DUAL resolution:`);
        console.log(`   └─ Full-res: 1200×2000 PORTRAIT (source: ${nativeWidth}×${nativeHeight}) - for AI/Admin`);
        console.log(`   └─ Thumbnail: 300×500 PORTRAIT - for list views`);
        console.log(`   └─ Full-res size: ${Math.round(fullResData.length / 1024)}KB`);
        
        if (onCapture) {
            // Pass both images to parent
            onCapture(fullResData, thumbnailData);
        }
    };

    // 🔒 AGGRESSIVE scroll lock - NO scrolling on camera page AT ALL
    useEffect(() => {
        // Save original styles
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const originalBodyPosition = document.body.style.position;
        const originalBodyTop = document.body.style.top;
        const originalBodyLeft = document.body.style.left;
        const originalBodyRight = document.body.style.right;
        const originalBodyTouchAction = document.body.style.touchAction;
        const originalHtmlTouchAction = document.documentElement.style.touchAction;
        
        // Lock EVERYTHING
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = '0';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.touchAction = 'none';
        document.documentElement.style.touchAction = 'none';
        
        // Prevent ALL touch move events on the document
        const preventScroll = (e) => {
            // Only allow scroll inside elements with data-allow-scroll
            if (!e.target.closest('[data-allow-scroll]')) {
                e.preventDefault();
            }
        };
        
        document.addEventListener('touchmove', preventScroll, { passive: false });
        document.addEventListener('wheel', preventScroll, { passive: false });
        
        return () => {
            // Restore original styles
            document.body.style.overflow = originalBodyOverflow;
            document.documentElement.style.overflow = originalHtmlOverflow;
            document.body.style.position = originalBodyPosition;
            document.body.style.top = originalBodyTop;
            document.body.style.left = originalBodyLeft;
            document.body.style.right = originalBodyRight;
            document.body.style.touchAction = originalBodyTouchAction;
            document.documentElement.style.touchAction = originalHtmlTouchAction;
            document.removeEventListener('touchmove', preventScroll);
            document.removeEventListener('wheel', preventScroll);
        };
    }, []);

    return (
        <div 
          className="fixed inset-0 z-50 flex flex-col bg-black" 
          style={{
            zIndex: 999999,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: 'var(--app-height, 100svh)',
            maxHeight: 'var(--app-height, 100svh)',
            maxWidth: '100vw',
            minHeight: '-webkit-fill-available',
            minWidth: '320px',
            paddingTop: 'calc(80px + env(safe-area-inset-top, 0px))',
            paddingBottom: 'calc(70px + env(safe-area-inset-bottom, 0px))',
            overflow: 'hidden',
            overflowX: 'hidden',
            overflowY: 'hidden',
            overscrollBehavior: 'none',
            touchAction: 'none'
          }}
        >
            {/* Header Section - HIDDEN (Main app header shows) */}
            <header className="bg-white backdrop-blur-sm border-b-2 border-[var(--pbfs-gray)] px-4 py-3" style={{ display: 'none' }}>
                {/* Top Row: Centered Logo */}
                <div className="flex items-center justify-center mb-2">
                    <button
                        onClick={() => {
                            if (onExit) {
                                onExit();
                            }
                        }}
                        className="hover:opacity-80 transition-opacity duration-300 cursor-pointer"
                        title="Close Camera"
                    >
                        <img
                            src="/images/PBFS-white.png"
                            alt="PBFS TagQuest"
                            className="h-12 w-auto object-contain"
                            onError={(e) => {
                                console.error('Logo failed to load, falling back to default');
                                e.target.src = '/images/new-tagquest-green-blue-logo.png';
                            }}
                        />
                    </button>
                </div>
                
                {/* Bottom Row: Centered User Dropdown */}
                <div className="flex justify-center items-center relative">
                    {/* Hidden Admin Toggle - positioned absolute top-right */}
                    {onAdminAccess && (
                        <button
                            onClick={onAdminAccess}
                            className="absolute right-0 text-[var(--pbfs-navy)]/20 hover:text-[var(--pbfs-navy)]/40 text-xs transition-all duration-300 px-1"
                            title="Admin Access"
                        >
                            .
                        </button>
                    )}
                    
                    {/* User Profile - Centered */}
                    {currentUser && (
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                                className="flex items-center space-x-2 px-2 py-1 bg-[var(--pbfs-navy)]/10 hover:bg-[var(--pbfs-navy)]/20 rounded-lg transition-all duration-300 border border-[var(--pbfs-navy)]/20"
                            >
                                <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                                    <span className="text-white font-bold text-xs">
                                        {currentUser.charAt(0).toUpperCase()}
                                    </span>
                                </div>
                                <span className="text-[var(--pbfs-navy)] text-xs font-medium hidden sm:inline">{currentUser}</span>
                                <div className="flex items-center space-x-1">
                                    <span className="text-yellow-400 text-xs">🏆</span>
                                    <span className="text-yellow-400 text-xs font-bold">{userPoints || 0}</span>
                                    <span className="text-[var(--pbfs-navy)]/60 text-xs">
                                        {showProfileDropdown ? '▲' : '▼'}
                                    </span>
                                </div>
                            </button>
                                
                                {showProfileDropdown && (
                                    <div 
                                        className="fixed w-56 max-w-[50vw] bg-[var(--pbfs-navy)] backdrop-blur-md rounded-lg border border-white/20 shadow-xl" 
                                        style={{ 
                                            zIndex: 99999999,
                                            top: '80px', // Below header
                                            right: '10px' // From right edge
                                        }}
                                    >
                                        <div className="p-3">
                                            <div 
                                                className="px-3 py-2 border-b border-white/10 mb-2"
                                                onClick={(e) => {
                                                    // Close dropdown when clicking on stats area (non-interactive parts)
                                                    if (e.target === e.currentTarget || e.target.closest('.non-interactive')) {
                                                        setShowProfileDropdown(false);
                                                    }
                                                }}
                                            >
                                                <p className="text-white font-bold text-base mb-1">{currentUser}</p>
                                                
                                                {/* Level Display */}
                                                {(() => {
                                                    const totalPoints = userPoints || 0;
                                                    const levels = [
                                                        { level: 1, name: "Safety Apprentice", min: 0, max: 3, icon: "👷" },
                                                        { level: 2, name: "Cadet Tag-ling", min: 4, max: 6, icon: "🎓" },
                                                        { level: 3, name: "Risk Wrangler", min: 7, max: 9, icon: "🤠" },
                                                        { level: 4, name: "Marshal of Defense", min: 10, max: 12, icon: "⭐" },
                                                        { level: 5, name: "Guardian of Safety", min: 13, max: 15, icon: "🛡️" },
                                                        { level: 6, name: "Chief Tag Officer", min: 16, max: 18, icon: "👑" },
                                                        { level: 7, name: "Safety Royalty", min: 19, max: 21, icon: "💎" },
                                                        { level: 8, name: "Legend of Life Safety", min: 22, max: 999999, icon: "🏆" }
                                                    ];
                                                    
                                                    let currentLevel = levels[0];
                                                    for (let i = levels.length - 1; i >= 0; i--) {
                                                        if (totalPoints >= levels[i].min) {
                                                            currentLevel = levels[i];
                                                            break;
                                                        }
                                                    }
                                                    
                                                    const nextLevel = levels[Math.min(currentLevel.level, levels.length - 1)];
                                                    const pointsInCurrentLevel = totalPoints - currentLevel.min;
                                                    const pointsNeededForLevel = nextLevel.min - currentLevel.min;
                                                    const pointsToNextLevel = nextLevel.min - totalPoints;
                                                    
                                                    return (
                                                        <>
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className="text-2xl">{currentLevel.icon}</span>
                                                                <div className="flex-1">
                                                                    <p className="text-yellow-400 font-semibold text-sm">{currentLevel.name}</p>
                                                                    <p className="text-white/50 text-xs">Level {currentLevel.level}</p>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Points & Progress */}
                                                            <div className="mb-2">
                                                                <div className="flex justify-between items-center mb-1">
                                                                    <span className="text-white/70 text-xs">Points</span>
                                                                    <span className="text-yellow-400 font-bold text-sm">{pointsInCurrentLevel} / {pointsNeededForLevel}</span>
                                                                </div>
                                                                <div className="w-full h-1.5 bg-[var(--pbfs-gray)] rounded-full overflow-hidden">
                                                                    <div 
                                                                        className="h-full bg-[var(--pbfs-green)] transition-all duration-500"
                                                                        style={{ width: `${Math.min(100, (pointsInCurrentLevel / pointsNeededForLevel) * 100)}%` }}
                                                                    />
                                                                </div>
                                                                <p className="text-white/40 text-xs mt-1">
                                                                    {currentLevel.level < 8 ? `${pointsToNextLevel} more to level ${currentLevel.level + 1}!` : 'Max level!'}
                                                                </p>
                                                            </div>
                                                            
                                                            {/* Badges */}
                                                            <div className="flex items-center gap-1 text-lg mb-2">
                                                                <span className="text-[var(--pbfs-gray)]" title="First scan!">🎯</span>
                                                                {totalPoints >= 5 ? <span className="text-[var(--pbfs-gray)]" title="Hot Streak">🔥</span> : <span className="text-white/30">🔥</span>}
                                                                {totalPoints >= 100 ? <span className="text-[var(--pbfs-gray)]" title="Tag-nificent!">💯</span> : <span className="text-white/30">💯</span>}
                                                            </div>
                                                            
                                                            {/* Quick Stats */}
                                                            <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                                                                <div className="bg-black/20 rounded px-2 py-1">
                                                                    <span className="text-white/50">Rank:</span>
                                                                    <span className="text-white font-bold ml-1">#{userRank || '?'}</span>
                                                                </div>
                                                                <div className="bg-black/20 rounded px-2 py-1">
                                                                    <span className="text-white/50">Streak:</span>
                                                                    <span className="text-white font-bold ml-1">0🔥</span>
                                                                </div>
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                                
                                                {/* How to Play - Clean, professional */}
                                                <details className="mt-2 bg-white/5 rounded-lg">
                                                    <summary className="text-[var(--pbfs-blue)] text-xs px-3 py-2 cursor-pointer hover:bg-white/10 rounded-lg transition-colors list-none">
                                                        How to Play
                                                    </summary>
                                                    <div className="px-3 py-3 text-white/70 text-xs space-y-2 leading-relaxed">
                                                        <p><strong>Scan tags</strong> to earn 1 point each</p>
                                                        <p><strong>Level up</strong> every 3 scans</p>
                                                        <p><strong>Earn badges</strong> for streaks & milestones</p>
                                                        <p><strong>Climb the leaderboard</strong> to win</p>
                                                    </div>
                                                </details>
                                                
                                                {/* Sound Toggle */}
                                                {onToggleSound && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onToggleSound();
                                                        }}
                                                        className="mt-2 w-full flex items-center justify-between px-2 py-1.5 bg-gray-500/10 hover:bg-gray-500/20 rounded-lg transition-colors"
                                                    >
                                                        <span className="text-white/70 text-xs">🔊 Sounds</span>
                                                        <div className={`w-10 h-5 rounded-full transition-colors ${soundEnabled ? 'bg-[var(--pbfs-green)]' : 'bg-gray-600'}`}>
                                                            <div className={`w-4 h-4 mt-0.5 rounded-full bg-white transition-transform ${soundEnabled ? 'ml-5' : 'ml-0.5'}`}></div>
                                                        </div>
                                                    </button>
                                                )}
                                            </div>
                                            
                                            {/* Menu Items */}
                                            <button 
                                                onClick={() => {
                                                    setShowProfileDropdown(false);
                                                    if (onNavigateLeaderboard) {
                                                        onNavigateLeaderboard();
                                                    }
                                                }}
                                                className="w-full text-left px-3 py-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10 rounded text-sm transition-colors font-semibold"
                                            >
                                                🏆 Leaderboard
                                            </button>
                                            <button 
                                                onClick={() => setShowProfileDropdown(false)}
                                                className="w-full text-left px-3 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded text-sm transition-colors"
                                            >
                                                👤 Edit Profile
                                            </button>
                                            <button 
                                                onClick={() => {
                                                    setShowProfileDropdown(false);
                                                    // Clear all localStorage data
                                                    localStorage.removeItem('pwaOnboardingComplete');
                                                    localStorage.removeItem('submittedOnBehalfOf');
                                                    localStorage.removeItem('cameraPermissionGranted');
                                                    localStorage.removeItem('locationPermissionGranted');
                                                    // Reload page to show onboarding
                                                    window.location.reload();
                                                }}
                                                className="w-full text-left px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded text-sm transition-colors"
                                            >
                                                🚪 Logout
                                            </button>
                                            <button 
                                                onClick={() => {
                                                    setShowProfileDropdown(false);
                                                    window.close();
                                                }}
                                                className="w-full text-left px-3 py-2 text-gray-400 hover:text-gray-300 hover:bg-gray-500/10 rounded text-sm transition-colors"
                                            >
                                                ✕ Close App
                                            </button>
                                        </div>
                                    </div>
                            )}
                        </div>
                    )}
                </div>
            </header>

            {/* Camera Preview Area - DYNAMIC: fills ALL space between header and bottom nav */}
            <div 
                className="flex flex-col items-center justify-between w-full"
                style={{
                    flex: '1 1 0',
                    minHeight: 0, // Critical for flex shrinking
                    overflow: 'hidden',
                    touchAction: 'none'
                }}
            >
                {/* Camera Frame Container - grows to fill available space */}
                <div 
                    className="relative w-full flex flex-col items-center justify-center" 
                    style={{ 
                        maxWidth: '500px',
                        minWidth: '320px',
                        flex: '1 1 0',
                        minHeight: 0, // Critical for flex shrinking
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        padding: '0 4px'
                    }}
                >
                    {/* Loading State */}
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center z-10">
                            <div className="bg-white/90 rounded-lg p-6 text-center">
                                <div className="loading-spinner mx-auto mb-4"></div>
                                <p className="text-gray-800">Initializing camera...</p>
                            </div>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div className="absolute inset-0 flex items-center justify-center z-10">
                            <div className="bg-red-100 border border-red-400 rounded-lg p-6 text-center max-w-md">
                                <h3 className="text-red-800 font-bold mb-2">Camera Error</h3>
                                <p className="text-red-700 text-sm mb-4">{error}</p>
                                <button
                                    onClick={onExit}
                                    className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Camera Frame - Fire Tag Shape - DYNAMIC: grows to fill available space */}
                    <div 
                        className="relative w-full bg-black/20 backdrop-blur-sm border border-solid border-[var(--pbfs-gray)]"
                        style={{
                            flex: '1 1 0',
                            minHeight: '200px',
                            maxHeight: 'calc(100% - 60px)', // Leave room for button
                            width: '100%',
                            clipPath: 'polygon(0 4%, 4% 0, 96% 0, 100% 4%, 100% 100%, 0 100%)'
                        }}
                    >
                        {/* Red Circle at Top (Fire Tag Hole) */}
                        <div 
                            className="absolute top-0 left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full border-4 border-red-500 bg-red-500/20"
                            style={{ marginTop: '-6px' }}
                        />
                        
                        {/* Video Feed */}
                        <video 
                            ref={videoRef} 
                            autoPlay 
                            playsInline 
                            muted
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{
                                clipPath: 'polygon(0 4%, 4% 0, 96% 0, 100% 4%, 100% 100%, 0 100%)'
                            }}
                        />
                        
                        {/* "PUT TAG IN FRAME" Overlay - Fire Tag Shape with cut top corners */}
                        {!isLoading && !error && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                                {/* Fire Tag Shape Frame - Cut corners at top like inspection tag */}
                                <div 
                                    className="relative"
                                    style={{
                                        width: '95%',
                                        height: '92%',
                                        animation: 'pulse 2s ease-in-out infinite',
                                    }}
                                >
                                    {/* SVG Fire Tag Shape with dashed border - More pronounced cuts */}
                                    <svg 
                                        className="absolute inset-0 w-full h-full"
                                        viewBox="0 0 100 160"
                                        preserveAspectRatio="none"
                                        style={{
                                            filter: 'drop-shadow(0 0 15px rgba(13, 177, 75, 0.4))'
                                        }}
                                    >
                                        {/* Fire tag shape path - larger cut corners (12 units = ~7.5%) */}
                                        <path 
                                            d="M 12 0 L 0 12 L 0 160 L 100 160 L 100 12 L 88 0 Z"
                                            fill="none"
                                            stroke="var(--pbfs-green)"
                                            strokeWidth="2"
                                            strokeDasharray="6,4"
                                            vectorEffect="non-scaling-stroke"
                                        />
                                    </svg>
                                    
                                    {/* Inner glow effect matching the fire tag shape */}
                                    <div 
                                        className="absolute inset-0"
                                        style={{
                                            clipPath: 'polygon(12% 0, 0 7.5%, 0 100%, 100% 100%, 100% 7.5%, 88% 0)',
                                            boxShadow: 'inset 0 0 40px rgba(13, 177, 75, 0.25)'
                                        }}
                                    />
                                    
                                    {/* "ALIGN TAG IN FRAME" Label - Top Center */}
                                    <div className="absolute top-3 left-1/2 transform -translate-x-1/2 bg-[var(--pbfs-green)] px-4 py-1 rounded-lg shadow-lg whitespace-nowrap">
                                        <span className="text-white font-bold text-xs tracking-wider uppercase drop-shadow-lg">
                                            ALIGN TAG IN FRAME
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Switch Camera Button - Top Right */}
                        {!isLoading && !error && (
                            <div className="absolute top-4 right-4 z-20">
                                <button
                                    onClick={switchCamera}
                                    className="bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300 transform hover:scale-105 shadow-lg"
                                    title={`Switch to ${facingMode === 'environment' ? 'front' : 'rear'} camera`}
                                >
                                    {/* Professional Camera Flip Icon - Circular Arrows */}
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                    
                    {/* Capture Button - Small gap above bottom nav */}
                    {!isLoading && !error && (
                        <div className="w-full flex-shrink-0 mt-0 mb-[7px]">
                            <button
                                onClick={capturePhoto}
                                className="w-full bg-[var(--pbfs-green)] hover:bg-opacity-90 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 shadow-2xl flex items-center justify-center space-x-3 text-base sm:text-lg relative overflow-hidden group"
                                style={{
                                    animation: 'breathe 3s ease-in-out infinite',
                                    minHeight: '52px'
                                }}
                            >
                                <span className="text-2xl relative z-10">📸</span>
                                <span className="relative z-10 tracking-wide font-eurostile">TAP TO CAPTURE</span>
                                {/* Hidden Upload Trigger (for testing) - tap the period */}
                                <span 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        fileInputRef.current?.click();
                                    }}
                                    className="absolute right-2 bottom-1 text-white/10 hover:text-white/30 cursor-pointer text-xs"
                                    title="Upload test image"
                                >
                                    .
                                </span>
                            </button>
                        </div>
                    )}
                    
                    {/* Hidden File Input for Testing */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                    />
                    
                    {/* Hidden Canvas for Capture */}
                    <canvas ref={canvasRef} className="hidden" />
                </div>
            </div>
        </div>
    );
};

export default FireExtinguisherCamera;
